/**
 * User menu — replaces the plain logout button in the sidebar. Opens a
 * small popover with profile info, deep links to settings, and the logout.
 *
 * Shows the user's alias (preferred) or displayName (from auth) or email,
 * and the avatar (custom → auth provider → Gravatar → initials, see UserAvatar).
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAuth } from '@/features/auth/authContext'
import { useTheme } from '@/features/theme/ThemeProvider'
import { toast } from '@/features/feedback/toast'
import { cn } from '@/lib/utils'
import { UserAvatar } from './UserAvatar'

export function UserMenu({ compact = false }: { compact?: boolean }) {
  const { user, logout } = useAuth()
  const { theme, themes, setTheme } = useTheme()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!user) return null

  const display = user.alias || user.displayName || user.email || '?'
  const avatarSources = [user.customAvatarUrl, user.avatarUrl]

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center gap-2 rounded-md border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 transition-colors hover:border-brand-500',
          compact ? 'p-1' : 'w-full px-2 py-1.5',
        )}
        title={display}
      >
        <UserAvatar name={display} email={user.email} sources={avatarSources} className="h-6 w-6 text-[10px]" pixelSize={48} decorative={!compact} />
        {!compact && (
          <div className="min-w-0 flex-1 text-left">
            <div className="truncate text-xs font-medium text-surface-800 dark:text-surface-100">{display}</div>
            <div className="truncate text-[10px] text-surface-500">{user.email}</div>
          </div>
        )}
        {!compact && (
          <span className="text-surface-400" aria-hidden>
            ⋯
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute bottom-full left-0 right-0 z-30 mb-1.5 overflow-hidden rounded-lg border border-surface-200 dark:border-surface-700 bg-app-elevated shadow-xl"
        >
          <div className="border-b border-surface-200 dark:border-surface-700 px-3 py-2">
            <div className="flex items-center gap-2">
              <UserAvatar name={display} email={user.email} sources={avatarSources} className="h-8 w-8 text-xs" decorative />
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-medium">{display}</div>
                <div className="truncate text-[10px] text-surface-500">{user.email}</div>
              </div>
            </div>
          </div>

          <Section title="Theme">
            {themes.map((t) => (
              <button
                key={t.id}
                role="menuitemradio"
                aria-checked={theme.id === t.id}
                onClick={() => {
                  setTheme(t.id)
                  setOpen(false)
                }}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-surface-100 dark:hover:bg-surface-800',
                  theme.id === t.id && 'text-brand-600 dark:text-brand-300',
                )}
              >
                <ThemeDot theme={t} />
                <span className="flex-1 truncate">{t.name}</span>
                {theme.id === t.id && (
                  <span aria-hidden className="text-brand-500">
                    ✓
                  </span>
                )}
              </button>
            ))}
          </Section>

          <div className="border-t border-surface-200 dark:border-surface-700" />

          <button
            role="menuitem"
            onClick={() => {
              setOpen(false)
              navigate({ to: '/settings' })
            }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-xs hover:bg-surface-100 dark:hover:bg-surface-800"
          >
            <span aria-hidden>⚙️</span>
            <span>Settings</span>
          </button>

          <div className="border-t border-surface-200 dark:border-surface-700" />

          <button
            role="menuitem"
            onClick={() => {
              setOpen(false)
              void logout().catch(() => toast('Could not sign out. Please try again.', { kind: 'error' }))
            }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-red-600 dark:text-red-300 hover:bg-red-500/10"
          >
            <span aria-hidden>↩</span>
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  )
}

function ThemeDot({ theme }: { theme: ReturnType<typeof useTheme>['themes'][number] }) {
  const t = theme.tokens
  return (
    <span
      aria-hidden
      className="inline-block h-3 w-3 rounded-full border border-surface-300 dark:border-surface-700"
      style={{ backgroundColor: `rgb(${t.accent})` }}
    />
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="py-1">
      <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-surface-500">
        {title}
      </div>
      {children}
    </div>
  )
}